import {
  removeNull,
  laborContractStatusLabel,
  canBatchReportLaborContract
} from '@/utils/handleData.js'
import { buildFileList, buildSrcList } from '@/utils/signedDoc'
import { decimalMul, roundMoney } from '@/utils/getCalculatedValue.js'

function toNumberOrNull(value) {
  if (value === '' || value == null) return null
  const num = Number(value)
  return Number.isNaN(num) ? value : num
}

function sanitizeBusinessContract(data) {
  const payload = Object.assign({}, data || {})
  payload.amount = toNumberOrNull(payload.amount)
  payload.taxRate = toNumberOrNull(payload.taxRate)
  if (Array.isArray(payload.details)) {
    payload.details = payload.details.map((item) =>
      Object.assign({}, item, {
        quantity: toNumberOrNull(item.quantity),
        unitPrice: toNumberOrNull(item.unitPrice),
        amount: toNumberOrNull(item.amount)
      })
    )
  }
  return payload
}

function contractDetailRoute(kind) {
  if (kind === 'purchase') return 'purchase'
  if (kind === 'sales') return 'sales'
  return 'piece'
}

function currentContractKind(rootState) {
  const label =
    (rootState.currentTab && rootState.currentTab.label) || ''
  return (
    {
      劳务计件合同: 'piece',
      其他支出合同: 'expense',
      材料采购合同: 'purchase',
      产品销售合同: 'sales'
    }[label] || 'piece'
  )
}

function contractDrawerUid(rootState, drawerName, payload) {
  if (typeof payload === 'string' && payload) return payload
  return (
    (payload && payload.contractUid) ||
    (rootState.currentUids && rootState.currentUids[drawerName]) ||
    ''
  )
}

function calculateLineAmount(item) {
  return roundMoney(
    decimalMul(
      item && item.quantity != null ? Number(item.quantity) : 0,
      item && item.unitPrice != null ? Number(item.unitPrice) : 0
    )
  )
}

function sanitizeContractDetail(data) {
  const payload = Object.assign({}, data || {})
  payload.unitPrice = toNumberOrNull(payload.unitPrice)
  payload.quantity = toNumberOrNull(payload.quantity)
  payload.amount = calculateLineAmount(payload)
  if (payload.contractKind === 'piece') {
    payload.measurementRule = ''
    payload.qualityStandard = ''
  }
  return payload
}

function sanitizeLedgerDetails(data) {
  const payload = Object.assign({}, data || {})
  delete payload.detailOptions
  payload.deductionAmount = toNumberOrNull(payload.deductionAmount)
  payload.details = (Array.isArray(payload.details) ? payload.details : []).map(
    (item) => {
      const line = Object.assign({}, item)
      line.quantity = toNumberOrNull(line.quantity)
      line.unitPrice = toNumberOrNull(line.unitPrice)
      line.amount = calculateLineAmount(line)
      return line
    }
  )
  return payload
}

function normalizeFileUids(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

function sanitizeSettlement(data) {
  const payload = sanitizeLedgerDetails(data)
  payload.invoiceFiles = normalizeFileUids(payload.invoiceFiles).join(',')
  delete payload.fileLists
  return payload
}

function decorateContractFiles(row, rootState) {
  if (!row) return row
  const contractUids = normalizeFileUids(row.contractFiles)
  row.contractFiles = contractUids
  row.fileLists = {
    contractFiles: buildFileList(
      { state: rootState },
      contractUids.join(','),
      (row.contractName || '合同') + '-合同文本'
    )
  }
  row.contractAttachment = contractUids.length + '份合同文本'
  return row
}

export default {
  namespaced: true,
  state: {
    contractType: [], // 缓存用户权限下合同类型
    laborContractList: {}, // 缓存劳动合同列表当前页
    allLaborContracts: [], // 缓存某人全部备案劳动合同
    laborContractInfo: {}, // 缓存当前查看的劳动合同信息
    pieceContractList: { result: [], totalSize: 0 },
    otherExpenseContractList: { result: [], totalSize: 0 },
    purchaseContractList: {}, // 缓存采购合同列表当前页
    salesContractList: {}, // 缓存销售合同列表当前页
    purchaseContractInfo: {}, // 缓存当前查看的采购合同
    salesContractInfo: {}, // 缓存当前查看的销售合同
    contractDetailRows: [],
    pieceSettlementRows: [],
    salesShipmentRows: [],
    contractInvoiceRows: [],
    purchaseAcceptanceCandidates: []
  },
  getters: {},
  mutations: {},
  actions: {
    /********************************************************** 通用相关查询 ********************************************************/

    // 获取用户权限下合同类型
    async getContractType({ commit, dispatch }) {
      const res = await dispatch(
        'baseRequest',
        { method: 'GET', url: '/rule/templatecontract/find' },
        { root: true }
      )
      if (res.data) {
        commit(
          'baseSet',
          { module: 'contract', key: 'contractType', value: res.data },
          { root: true }
        )
      }
    },

    async addContractDetail({ dispatch }, data) {
      const payload = sanitizeContractDetail(data)
      return await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url:
            '/organize/contract/' +
            contractDetailRoute(payload.contractKind) +
            '/details',
          data: payload,
          msg: '保存成功'
        },
        { root: true }
      )
    },

    async editContractDetail({ dispatch }, data) {
      const payload = sanitizeContractDetail(data)
      return await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url:
            '/organize/contract/' +
            contractDetailRoute(payload.contractKind) +
            '/details',
          data: payload,
          msg: '保存成功'
        },
        { root: true }
      )
    },

    async addContractSettlement({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/contract/piece/settlements',
          data: sanitizeSettlement(data),
          msg: '保存成功'
        },
        { root: true }
      )
    },

    async editContractSettlement({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/contract/piece/settlements',
          data: sanitizeSettlement(data),
          msg: '保存成功'
        },
        { root: true }
      )
    },

    async addContractShipment({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/contract/sales/shipments',
          data: sanitizeLedgerDetails(data),
          msg: '保存成功'
        },
        { root: true }
      )
    },

    async editContractShipment({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/contract/sales/shipments',
          data: sanitizeLedgerDetails(data),
          msg: '保存成功'
        },
        { root: true }
      )
    },

    async addContractInvoice({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/contract/invoices',
          data: data,
          msg: '保存成功'
        },
        { root: true }
      )
    },

    async editContractInvoice({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/contract/invoices',
          data: data,
          msg: '保存成功'
        },
        { root: true }
      )
    },

    async createContractAcceptance({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/goods/receipt',
          data: data,
          msg: '生成验收单成功'
        },
        { root: true }
      )
    },

    async loadContractDetailRows(
      { rootState, commit, dispatch },
      payload = {}
    ) {
      const contractUid = contractDrawerUid(
        rootState,
        'contractDetailsDrawer',
        payload
      )
      const contractKind = currentContractKind(rootState)
      if (!contractUid) {
        commit(
          'baseSet',
          { module: 'contract', key: 'contractDetailRows', value: [] },
          { root: true }
        )
        return { contractUid: '', contractKind }
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url:
            '/organize/contract/' +
            contractDetailRoute(contractKind) +
            '/details',
          data: { contractUid }
        },
        { root: true }
      )
      const rows = Array.isArray(res && res.data) ? res.data : []
      rows.forEach((row) => {
        if (row && !row.contractKind) row.contractKind = contractKind
      })
      commit(
        'baseSet',
        { module: 'contract', key: 'contractDetailRows', value: rows },
        { root: true }
      )
      return { contractUid, contractKind }
    },

    async loadPieceSettlementRows(
      { rootState, commit, dispatch },
      payload = {}
    ) {
      const contractUid = contractDrawerUid(
        rootState,
        'contractSettlementsDrawer',
        payload
      )
      if (!contractUid) {
        commit(
          'baseSet',
          { module: 'contract', key: 'pieceSettlementRows', value: [] },
          { root: true }
        )
        return { contractUid: '' }
      }
      const [settlementRes, detailRes] = await Promise.all([
        dispatch(
          'baseRequest',
          {
            method: 'GET',
            url: '/organize/contract/piece/settlements',
            data: { contractUid }
          },
          { root: true }
        ),
        dispatch(
          'baseRequest',
          {
            method: 'GET',
            url: '/organize/contract/piece/details',
            data: { contractUid }
          },
          { root: true }
        )
      ])
      const settlementRows = Array.isArray(settlementRes && settlementRes.data)
        ? settlementRes.data
        : []
      settlementRows.forEach((row) => {
        row.invoiceAttachment = normalizeFileUids(row.invoiceFiles).length
          ? '1份发票'
          : ''
      })
      const detailRows = Array.isArray(detailRes && detailRes.data)
        ? detailRes.data
        : []
      commit(
        'baseSet',
        { module: 'contract', key: 'pieceSettlementRows', value: settlementRows },
        { root: true }
      )
      commit(
        'baseSet',
        { module: 'contract', key: 'contractDetailRows', value: detailRows },
        { root: true }
      )
      return { contractUid }
    },

    async loadSalesShipmentRows(
      { rootState, commit, dispatch },
      payload = {}
    ) {
      const contractUid = contractDrawerUid(
        rootState,
        'contractShipmentsDrawer',
        payload
      )
      if (!contractUid) {
        commit(
          'baseSet',
          { module: 'contract', key: 'salesShipmentRows', value: [] },
          { root: true }
        )
        return { contractUid: '' }
      }
      const [shipmentRes, detailRes] = await Promise.all([
        dispatch(
          'baseRequest',
          {
            method: 'GET',
            url: '/organize/contract/sales/shipments',
            data: { contractUid }
          },
          { root: true }
        ),
        dispatch(
          'baseRequest',
          {
            method: 'GET',
            url: '/organize/contract/sales/details',
            data: { contractUid }
          },
          { root: true }
        )
      ])
      const shipmentRows = Array.isArray(shipmentRes && shipmentRes.data)
        ? shipmentRes.data
        : []
      const detailRows = Array.isArray(detailRes && detailRes.data)
        ? detailRes.data
        : []
      commit(
        'baseSet',
        { module: 'contract', key: 'salesShipmentRows', value: shipmentRows },
        { root: true }
      )
      commit(
        'baseSet',
        { module: 'contract', key: 'contractDetailRows', value: detailRows },
        { root: true }
      )
      return { contractUid }
    },

    async loadContractInvoiceRows(
      { rootState, commit, dispatch },
      payload = {}
    ) {
      const contractUid = contractDrawerUid(
        rootState,
        'contractInvoicesDrawer',
        payload
      )
      const contractType = currentContractKind(rootState)
      if (!contractUid) {
        commit(
          'baseSet',
          { module: 'contract', key: 'contractInvoiceRows', value: [] },
          { root: true }
        )
        return { contractUid: '', contractType }
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/contract/invoices',
          data: { contractType, contractUid }
        },
        { root: true }
      )
      const rows = Array.isArray(res && res.data) ? res.data : []
      commit(
        'baseSet',
        { module: 'contract', key: 'contractInvoiceRows', value: rows },
        { root: true }
      )
      return { contractUid, contractType }
    },

    async loadPurchaseAcceptanceRows(
      { rootState, commit, dispatch },
      payload = {}
    ) {
      const contractUid = contractDrawerUid(
        rootState,
        'contractAcceptanceDrawer',
        payload
      )
      if (!contractUid) {
        commit(
          'baseSet',
          {
            module: 'contract',
            key: 'purchaseAcceptanceCandidates',
            value: []
          },
          { root: true }
        )
        return { contractUid: '' }
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/contract/purchase/candidates',
          data: { uid: contractUid }
        },
        { root: true }
      )
      const rows = Array.isArray(res && res.data) ? res.data : []
      commit(
        'baseSet',
        {
          module: 'contract',
          key: 'purchaseAcceptanceCandidates',
          value: rows
        },
        { root: true }
      )
      commit(
        'selectionSet',
        { key: 'contractAcceptanceDrawer', value: rows.slice() },
        { root: true }
      )
      return { contractUid }
    },

    prepareContractDetailForm({ rootState }, row = {}) {
      const values = JSON.parse(JSON.stringify(row || {}))
      values.contractKind = values.contractKind || currentContractKind(rootState)
      if (values.contractKind === 'piece') {
        values.workContent = [
          values.workContent,
          values.measurementRule ? '计量规则：' + values.measurementRule : '',
          values.qualityStandard ? '质量标准：' + values.qualityStandard : ''
        ]
          .filter((item) => String(item || '').trim())
          .join('\n')
        values.measurementRule = ''
        values.qualityStandard = ''
      }
      return values
    },

    prepareContractSettlementForm({ rootState }, row = {}) {
      const values = JSON.parse(JSON.stringify(row || {}))
      const invoiceUids = normalizeFileUids(values.invoiceFiles)
      values.invoiceFiles = invoiceUids
      values.fileLists = {
        invoiceFiles: buildFileList(
          { state: rootState },
          invoiceUids.join(','),
          (values.settlementNo || values.settlementName || '结算单') +
            '-合同发票'
        )
      }
      values.detailOptions = (
        rootState.contract.contractDetailRows || []
      ).slice()
      return values
    },

    prepareContractShipmentForm({ rootState }, row = {}) {
      return Object.assign({}, JSON.parse(JSON.stringify(row || {})), {
        detailOptions: (rootState.contract.contractDetailRows || []).slice()
      })
    },

    async deleteContractDetail({ rootState, dispatch }, uid) {
      return await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url:
            '/organize/contract/' +
            contractDetailRoute(currentContractKind(rootState)) +
            '/details',
          data: { uid },
          msg: '删除成功'
        },
        { root: true }
      )
    },

    async deleteContractSettlement({ dispatch }, uid) {
      return await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/contract/piece/settlements',
          data: { uid },
          msg: '删除成功'
        },
        { root: true }
      )
    },

    async deleteContractShipment({ dispatch }, uid) {
      return await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/contract/sales/shipments',
          data: { uid },
          msg: '删除成功'
        },
        { root: true }
      )
    },

    async deleteContractInvoice({ dispatch }, uid) {
      return await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/contract/invoices',
          data: { uid },
          msg: '删除成功'
        },
        { root: true }
      )
    },

    /***********************************************************  劳动合同 *****************************************************/

    // 变更（新增）劳动合同
    async addLaborContract({ dispatch }, data) {
      const payload =
        data && typeof data === 'object' ? Object.assign({}, data) : {}
      // 变更不继承旧合同图片，须在新合同上重新签署上传
      payload.picUid = ''
      if (payload.fileLists && typeof payload.fileLists === 'object') {
        payload.fileLists = Object.assign({}, payload.fileLists, {
          picUid: []
        })
      }
      payload.srcList = []
      const res = await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/rule/contract/hvproject',
          data: payload,
          msg: '变更劳动合同成功'
        },
        { root: true }
      )
      return res
    },
    // 获取劳动合同列表
    async getLaborContractList({ rootState, commit, dispatch }) {
      var queryValues = await dispatch(
        'bidSection/ensureDefaultBidQuery',
        { queryKey: 'laborContract', fieldKey: 'vProjectUid' },
        { root: true }
      )
      queryValues = Object.assign({}, queryValues)
      queryValues.page = rootState.page.laborContract
      queryValues.limit = rootState.limit.laborContract
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/contract/treportedlistlimit',
          data: queryValues
        },
        { root: true }
      )
      var laborContractList = res.data ? res.data : {}
      if (!Array.isArray(laborContractList.resultData)) {
        laborContractList.resultData = []
      }
      laborContractList.resultData.forEach((item) => {
        item.disabledButtons = []
        item.fileLists = {}
        item.statusCode = String(item.status == null ? '' : item.status).trim()
        if (!item.picUid) {
          item.isPic = false
          item.picStatus = '预览合同'
          item.srcList = []
        } else {
          item.isPic = true
          item.picStatus = '合同图片'
          item.srcList = buildSrcList({ state: rootState }, item.picUid)
        }
        item.status = laborContractStatusLabel(item.statusCode)
        item.refuseMark = String(
          item.refuseMark || item.refuse_mark || item.mark || ''
        ).trim()
        if (item.status === '已备案' && String(item.isChange) === '1') {
          item.status = '变更审核中'
        }
        if (item.status === '已备案') {
          item.disabledButtons = ['', 1, '']
        } else if (
          item.status === '报备审核中' ||
          item.status === '变更审核中' ||
          item.status === '已终止'
        ) {
          item.disabledButtons = ['', 1, 2]
        } else {
          item.disabledButtons = [0, '', 2]
        }
        if (item.isExit === '1' || item.isExit === 1) {
          item.isExit = '在场'
        } else if (item.isExit === '0' || item.isExit === 0) {
          item.isExit = '退场'
        }
        item.unselectable = !canBatchReportLaborContract(item)
        if (
          String(item.vProjectUid || '') !== String(rootState.user.orgValue || '') ||
          item.isExit !== '在场'
        ) {
          item.disabledButtons = ['', 1, 2]
        }
      })
      commit(
        'baseSet',
        {
          module: 'contract',
          key: 'laborContractList',
          value: laborContractList
        },
        { root: true }
      )
    },
    // 个人某机构全部备案劳动合同
    async getLaborContractInfo({ rootState, commit, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/contract/singledetail',
          data: { contractUid: uid }
        },
        { root: true }
      )
      // console.log(res)
      var allLaborContracts = JSON.parse(
        JSON.stringify(res.data ? res.data : [])
      )
      var laborContractInfo = {}
      for (let index = 0; index < allLaborContracts.length; index++) {
        const item = allLaborContracts[index]
        item.uid = item.contractUid
        item.value = item.contractUid
        item.label =
          item.status !== '0'
            ? item.contractNo +
              '\xa0\xa0\xa0\xa0' +
              (item.status !== '1' ? '签字或审核中' : '执行中')
            : item.contractNo + '\xa0\xa0\xa0\xa0' + '已终止'
        item.fileLists = {}
        item.fileLists.picUid = []
        if (item.picUid) {
          item.fileLists.picUid = buildFileList(
            { state: rootState },
            item.picUid,
            (item.personName || '') + '电子劳动合同'
          )
          item.srcList = buildSrcList({ state: rootState }, item.picUid)
        }
        if (index === allLaborContracts.length - 1) {
          commit(
            'baseSet',
            {
              module: 'contract',
              key: 'allLaborContracts',
              value: allLaborContracts
            },
            { root: true }
          )
          const isStatus1 = allLaborContracts.find((item) => {
            return item.status === '1'
          })
          const isStatus3 = allLaborContracts.find((item) => {
            return item.status === '3'
          })
          if (isStatus1) {
            laborContractInfo = isStatus1
          } else if (!isStatus1 && isStatus3) {
            laborContractInfo = isStatus3
          } else {
            laborContractInfo = allLaborContracts[0]
          }
          // 优先使用当前点开的合同（待签名/被拒绝等），避免回显成历史已备案
          const matched = allLaborContracts.find((item) => {
            const id = String(item.contractUid || item.uid || '')
            return id && id === String(uid || '')
          })
          if (matched) {
            laborContractInfo = matched
          }
          commit(
            'baseSet',
            {
              module: 'contract',
              key: 'laborContractInfo',
              value: laborContractInfo
            },
            { root: true }
          )
        }
      }
      return laborContractInfo
    },
    // 报备（编辑）劳动合同信息
    async editLaborContract({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/rule/contract/hvproject',
          data: data,
          msg: '报备劳动合同成功'
        },
        { root: true }
      )
      return res
    },
    // 报备（编辑）劳动合同信息
    async batchReportLaborContract({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/rule/contract/hvprojects',
          data: data,
          msg: '批量报备劳动合同成功'
        },
        { root: true }
      )
      return res
    },
    // 更新合同图片uid
    async updateLaborContractPic({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/person/worker/updatepic',
          data: data,
          msg: '上传劳动合同图片成功'
        },
        { root: true }
      )
      return res
    },
    /** 待办：劳动合同备案/变更审核（个人提交审核流） */
    async approveLaborContractFlow({ dispatch }, data) {
      const payload = data || {}
      return await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/act/flow/contrapproval',
          data: {
            uid: payload.flowUid || payload.uid,
            taskStatus: payload.taskStatus,
            mark: payload.mark || ''
          },
          msg:
            String(payload.taskStatus) === '1' || payload.taskStatus === 1
              ? '审核通过'
              : '已拒绝'
        },
        { root: true }
      )
    },

    /***********************************************************  采购合同 / 销售合同  *****************************************************/

    async getPieceContractList({ dispatch }) {
      return dispatch('getUnitPriceContractListByKind', {
        kind: 'piece',
        stateKey: 'pieceContractList'
      })
    },

    async getOtherExpenseContractList({ dispatch }) {
      return dispatch('getUnitPriceContractListByKind', {
        kind: 'expense',
        stateKey: 'otherExpenseContractList'
      })
    },

    async getUnitPriceContractListByKind(
      { rootState, commit, dispatch },
      { kind, stateKey }
    ) {
      const queryKey = kind === 'piece' ? 'pieceContract' : 'otherExpenseContract'
      const query = Object.assign(
        {
          orgUid: rootState.user.orgValue,
          query: '',
          status: ''
        },
        (rootState.queryValues[queryKey] || {})
      )
      query.page = rootState.page[queryKey] || 1
      query.limit = rootState.limit[queryKey] || 10
      query.contractKind = kind
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/contract/piece/list',
          data: removeNull(query)
        },
        { root: true }
      )
      const list = res.data ? res.data : {}
      if (!Array.isArray(list.result)) list.result = []
      list.result.forEach((item) => decorateContractFiles(item, rootState))
      commit(
        'baseSet',
        { module: 'contract', key: stateKey, value: list },
        { root: true }
      )
      return list
    },

    async getUnitPriceContractInfo({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/contract/piece/info',
          data: { uid }
        },
        { root: true }
      )
      return res.data || {}
    },

    async addUnitPriceContract({ rootState, dispatch }, data) {
      const kind =
        rootState.currentTab.label === '其他支出合同' ? 'expense' : 'piece'
      return dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/contract/piece',
          data: sanitizeBusinessContract(
            Object.assign({}, data, { contractKind: kind })
          ),
          msg: '新增合同成功'
        },
        { root: true }
      )
    },

    async editUnitPriceContract({ rootState, dispatch }, data) {
      const kind =
        rootState.currentTab.label === '其他支出合同' ? 'expense' : 'piece'
      return dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/contract/piece',
          data: sanitizeBusinessContract(
            Object.assign({}, data, { contractKind: kind })
          ),
          msg: '编辑合同成功'
        },
        { root: true }
      )
    },

    async deleteUnitPriceContract({ dispatch }, uid) {
      return dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/contract/piece',
          data: { uid },
          msg: '删除合同成功'
        },
        { root: true }
      )
    },

    async terminatePieceContract({ dispatch }, uid) {
      return dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/contract/piece/terminate',
          data: { uid },
          msg: '合同终止成功'
        },
        { root: true }
      )
    },

    async saveContractAttachments({ rootState, dispatch }, data) {
      const label = rootState.currentTab.label
      const contractType =
        label === '材料采购合同'
          ? 'purchase'
          : label === '产品销售合同'
          ? 'sales'
          : label === '其他支出合同'
          ? 'expense'
          : 'piece'
      const payload = Object.assign({}, data || {}, {
        contractType,
        contractFiles: normalizeFileUids(data && data.contractFiles).join(',')
      })
      delete payload.fileLists
      delete payload.invoiceFiles
      return dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/contract/files',
          data: payload,
          msg: '合同附件保存成功'
        },
        { root: true }
      )
    },

    async submitContractAudit({ rootState, dispatch }, uid) {
      const label = rootState.currentTab.label
      const route =
        label === '材料采购合同'
          ? 'purchase'
          : label === '产品销售合同'
          ? 'sales'
          : 'piece'
      return dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/contract/' + route + '/submit',
          data: { uid },
          msg: '提交审核成功'
        },
        { root: true }
      )
    },

    async getPieceRateContractInfo({ commit, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/contract/piece/info',
          data: { uid }
        },
        { root: true }
      )
      const info = res.data ? res.data : {}
      if (!Array.isArray(info.details)) info.details = []
      commit(
        'baseSet',
        { module: 'contract', key: 'pieceRateContractInfo', value: info },
        { root: true }
      )
      return info
    },

    async approvePieceRateContract({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/contract/piece/audit',
          data: data
        },
        { root: true }
      )
    },

    async getPurchaseContractList({ rootState, commit, dispatch }, data) {
      const query = Object.assign(
        {
          orgUid: rootState.user.orgValue,
          query: '',
          status: '',
          category: ''
        },
        (rootState.queryValues.purchaseContract || {}),
        data || {}
      )
      query.page = rootState.page.purchaseContract
      query.limit = rootState.limit.purchaseContract
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/contract/purchase/list',
          data: removeNull(query)
        },
        { root: true }
      )
      const list = res.data ? res.data : {}
      if (!Array.isArray(list.result)) list.result = []
      list.result.forEach((item) => decorateContractFiles(item, rootState))
      commit(
        'baseSet',
        { module: 'contract', key: 'purchaseContractList', value: list },
        { root: true }
      )
    },

    async getSalesContractList({ rootState, commit, dispatch }, data) {
      const query = Object.assign(
        {
          orgUid: rootState.user.orgValue,
          query: '',
          status: '',
          category: ''
        },
        (rootState.queryValues.salesContract || {}),
        data || {}
      )
      query.page = rootState.page.salesContract
      query.limit = rootState.limit.salesContract
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/contract/sales/list',
          data: removeNull(query)
        },
        { root: true }
      )
      const list = res.data ? res.data : {}
      if (!Array.isArray(list.result)) list.result = []
      list.result.forEach((item) => decorateContractFiles(item, rootState))
      commit(
        'baseSet',
        { module: 'contract', key: 'salesContractList', value: list },
        { root: true }
      )
    },

    async getPurchaseContractInfo({ commit, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/contract/purchase/info',
          data: { uid }
        },
        { root: true }
      )
      const info = res.data ? res.data : {}
      if (!Array.isArray(info.details)) info.details = []
      commit(
        'baseSet',
        { module: 'contract', key: 'purchaseContractInfo', value: info },
        { root: true }
      )
      return info
    },

    async getSalesContractInfo({ commit, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/contract/sales/info',
          data: { uid }
        },
        { root: true }
      )
      const info = res.data ? res.data : {}
      if (!Array.isArray(info.details)) info.details = []
      commit(
        'baseSet',
        { module: 'contract', key: 'salesContractInfo', value: info },
        { root: true }
      )
      return info
    },

    async addPurchaseContract({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/contract/purchase',
          data: sanitizeBusinessContract(data),
          msg: '新增采购合同成功'
        },
        { root: true }
      )
    },

    async editPurchaseContract({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/contract/purchase',
          data: sanitizeBusinessContract(data),
          msg: '编辑采购合同成功'
        },
        { root: true }
      )
    },

    async deletePurchaseContract({ dispatch }, uid) {
      return await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/contract/purchase',
          data: { uid },
          msg: '删除采购合同成功'
        },
        { root: true }
      )
    },

    async addSalesContract({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/contract/sales',
          data: sanitizeBusinessContract(data),
          msg: '新增销售合同成功'
        },
        { root: true }
      )
    },

    async editSalesContract({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/contract/sales',
          data: sanitizeBusinessContract(data),
          msg: '编辑销售合同成功'
        },
        { root: true }
      )
    },

    async deleteSalesContract({ dispatch }, uid) {
      return await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/contract/sales',
          data: { uid },
          msg: '删除销售合同成功'
        },
        { root: true }
      )
    },

    async approvePurchaseContract({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/contract/purchase/audit',
          data: data
        },
        { root: true }
      )
    },

    async approveSalesContract({ dispatch }, data) {
      return await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/contract/sales/audit',
          data: data
        },
        { root: true }
      )
    }
  }
}
